import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { loadState, saveState, clearState } from 'states/localStorage';

const market = 'SE';

const initialUser = {
  market,
  language: market,
  ssn: false,
  signicatUrl: false,
  signicatProcessId: false,
  authToken: '',
  tokenExpires: 0,
  tokenType: '',
  tokenUpdated: 0,
  userRoles: [],
  name: '',
  contact_db_id: null,
  roles: []
};

// Load persisted state
const persistedData = loadState();
const initialState = {
  User: persistedData?.auth?.User || initialUser,
  AuthStatus: persistedData?.auth?.AuthStatus || null
};

function authReducer(state, action) {
  switch (action.type) {
    case 'GET': {
      const newUser = { ...state.User, ...action.payload };
      const newState = { ...state, User: newUser };
      
      // Save to localStorage
      saveState(
        {
          auth: {
            User: newUser,
            AuthStatus: newState.AuthStatus,
          }
        },
        {
          authToken: newUser.authToken,
          tokenExpires: newUser.tokenExpires,
        }
      );
      
      return newState;
    }
    
    case 'REFRESHTOKEN': {
      const { authToken, tokenExpires, tokenType } = action.payload?.data || {};
      
      if (tokenExpires) {
        const newUser = {
          ...state.User,
          authToken,
          tokenExpires,
          tokenType,
          tokenUpdated: Date.now()
        };
        
        const newState = { ...state, User: newUser };
        
        // Save to localStorage
        saveState(
          {
            auth: {
              User: newUser,
              AuthStatus: newState.AuthStatus,
            }
          },
          {
            authToken: newUser.authToken,
            tokenExpires: newUser.tokenExpires,
          }
        );
        
        return newState;
      }
      return state;
    }
    
    case 'LOGOUT': {
      clearState();
      return {
        User: { ...initialUser },
        AuthStatus: null
      };
    }
    
    default:
      return state;
  }
}

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const updateUser = (user) => {
    dispatch({ type: 'GET', payload: user });
  };

  const refreshToken = (tokenData) => {
    dispatch({ type: 'REFRESHTOKEN', payload: tokenData });
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  useEffect(() => {
    // Clear auth immediately if token already expired on boot
    if (state?.User?.tokenExpires && state.User.tokenExpires < Date.now()) {
      dispatch({ type: 'LOGOUT' });
    }
  }, [state?.User?.tokenExpires]);

  return (
    <AuthContext.Provider value={{
      state,
      dispatch,
      updateUser,
      refreshToken,
      logout,
      // Convenience getters for common access patterns
      user: state.User,
      authToken: state.User.authToken,
      tokenExpires: state.User.tokenExpires,
      userRoles: state.User.userRoles,
      isAuthenticated: !!state.User.authToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};
